import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

export const fetchCompanyIntegrations = createAsyncThunk(
  'companyIntegrations/fetchCompanyIntegrations',
  async ({ companyId, filters = {} } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(`/companies/${companyId}/integrations/delivery`, {
        params: { ...filters },
      })
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const fetchCompanyIntegration = createAsyncThunk(
  'companyIntegrations/fetchCompanyIntegration',
  async ({ companyId, integrationId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!integrationId) throw new Error('integrationId is required')

      const response = await axiosClient.get(
        `/companies/${companyId}/integrations/delivery/${integrationId}`
      )

      return {
        data: response.data,
        companyId: String(companyId),
        integrationId: String(integrationId),
      }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const fetchCompanyIntegrationTypes = createAsyncThunk(
  'companyIntegrations/fetchCompanyIntegrationTypes',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.get(`/companies/${companyId}/integrations/delivery/types`)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const createCompanyIntegration = createAsyncThunk(
  'companyIntegrations/createCompanyIntegration',
  async ({ companyId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      const response = await axiosClient.post(`/companies/${companyId}/integrations/delivery`, payload)
      return { data: response.data, companyId: String(companyId) }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const updateCompanyIntegration = createAsyncThunk(
  'companyIntegrations/updateCompanyIntegration',
  async ({ companyId, integrationId, payload } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!integrationId) throw new Error('integrationId is required')

      const response = await axiosClient.put(
        `/companies/${companyId}/integrations/delivery/${integrationId}`,
        payload
      )

      return {
        data: response.data,
        companyId: String(companyId),
        integrationId: String(integrationId),
      }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const toggleCompanyIntegrations = createAsyncThunk(
  'companyIntegrations/toggleCompanyIntegrations',
  async ({ companyId, integrationId, is_enabled } = {}, { rejectWithValue }) => {
    try {
      if (!companyId) throw new Error('companyId is required')
      if (!integrationId) throw new Error('integrationId is required')

      const response = await axiosClient.patch(
        `/companies/${companyId}/integrations/delivery/${integrationId}`,
        { is_enabled: Boolean(is_enabled) }
      )

      return {
        data: response.data,
        companyId: String(companyId),
        integrationId: String(integrationId),
      }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  listItems: {
    items: [],
    page: 1,
    page_size: 20,
    count: 0,
    next: null,
    previous: null,
  },

  currentIntegration: null,
  currentIntegrationStatus: 'idle',
  currentIntegrationError: null,
  currentIntegrationCompanyId: null,
  currentIntegrationId: null,

  types: [],
  status: 'idle',
  error: null,

  typesStatus: 'idle',
  typesError: null,

  createStatus: 'idle',
  createError: null,

  updateStatus: 'idle',
  updateError: null,

  toggleStatus: 'idle',
  toggleError: null,

  listCompanyId: null,
  typesCompanyId: null,
}

export const slice = createSlice({
  name: 'companyIntegrations',
  initialState,

  reducers: {
    setListItems(state, { payload }) {
      state.listItems = {
        ...state.listItems,
        ...payload,
      }
    },
    resetListItems(state) {
      state.listItems = { items: [], page: 1, page_size: 20, count: 0, next: null, previous: null }
      state.status = 'idle'
      state.error = null
      state.listCompanyId = null
    },
    resetCreateState(state) {
      state.createStatus = 'idle'
      state.createError = null
    },
    resetUpdateState(state) {
      state.updateStatus = 'idle'
      state.updateError = null
    },
    resetToggleState(state) {
      state.toggleStatus = 'idle'
      state.toggleError = null
    },
    resetCurrentIntegrationState(state) {
      state.currentIntegration = null
      state.currentIntegrationStatus = 'idle'
      state.currentIntegrationError = null
      state.currentIntegrationCompanyId = null
      state.currentIntegrationId = null
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyIntegrations.pending, (state, action) => {
        state.status = 'loading'
        state.error = null
        state.listCompanyId = String(action.meta.arg?.companyId ?? '')
      })
      .addCase(fetchCompanyIntegrations.fulfilled, (state, { payload }) => {
        const { data, companyId } = payload || {}
        const {
          results = [],
          count = 0,
          page = state.listItems.page,
          page_size = state.listItems.page_size,
          next = null,
          previous = null,
        } = data || {}

        state.status = 'succeeded'
        state.listCompanyId = companyId ?? state.listCompanyId
        state.listItems = {
          ...state.listItems,
          items: results,
          count,
          page,
          page_size,
          next,
          previous,
        }
      })
      .addCase(fetchCompanyIntegrations.rejected, (state, { payload }) => {
        state.status = 'failed'
        state.error = payload || 'Failed to fetch integrations'
      })

      .addCase(fetchCompanyIntegration.pending, (state, action) => {
        state.currentIntegrationStatus = 'loading'
        state.currentIntegrationError = null
        state.currentIntegration = null
        state.currentIntegrationCompanyId = String(action.meta.arg?.companyId ?? '')
        state.currentIntegrationId = String(action.meta.arg?.integrationId ?? '')
      })
      .addCase(fetchCompanyIntegration.fulfilled, (state, { payload }) => {
        state.currentIntegrationStatus = 'succeeded'
        state.currentIntegrationError = null
        state.currentIntegration = payload?.data || null
        state.currentIntegrationCompanyId = String(payload?.companyId ?? state.currentIntegrationCompanyId ?? '')
        state.currentIntegrationId = String(payload?.integrationId ?? state.currentIntegrationId ?? '')
      })
      .addCase(fetchCompanyIntegration.rejected, (state, { payload }) => {
        state.currentIntegrationStatus = 'failed'
        state.currentIntegrationError = payload || 'Failed to fetch integration'
      })

      .addCase(fetchCompanyIntegrationTypes.pending, (state, action) => {
        state.typesStatus = 'loading'
        state.typesError = null
        state.typesCompanyId = String(action.meta.arg?.companyId ?? '')
      })
      .addCase(fetchCompanyIntegrationTypes.fulfilled, (state, { payload }) => {
        const { data, companyId } = payload || {}
        state.typesStatus = 'succeeded'
        state.typesCompanyId = companyId ?? state.typesCompanyId
        state.types = Array.isArray(data) ? data : []
      })
      .addCase(fetchCompanyIntegrationTypes.rejected, (state, { payload }) => {
        state.typesStatus = 'failed'
        state.typesError = payload || 'Failed to fetch integration types'
      })

      .addCase(createCompanyIntegration.pending, (state) => {
        state.createStatus = 'loading'
        state.createError = null
      })
      .addCase(createCompanyIntegration.fulfilled, (state) => {
        state.createStatus = 'succeeded'
        state.createError = null
      })
      .addCase(createCompanyIntegration.rejected, (state, { payload }) => {
        state.createStatus = 'failed'
        state.createError = payload || 'Failed to create integration'
      })

      .addCase(updateCompanyIntegration.pending, (state) => {
        state.updateStatus = 'loading'
        state.updateError = null
      })
      .addCase(updateCompanyIntegration.fulfilled, (state) => {
        state.updateStatus = 'succeeded'
        state.updateError = null
      })
      .addCase(updateCompanyIntegration.rejected, (state, { payload }) => {
        state.updateStatus = 'failed'
        state.updateError = payload || 'Failed to update integration'
      })

      .addCase(toggleCompanyIntegrations.pending, (state) => {
        state.toggleStatus = 'loading'
        state.toggleError = null
      })
      .addCase(toggleCompanyIntegrations.fulfilled, (state, { payload }) => {
        state.toggleStatus = 'succeeded'
        state.toggleError = null

        const integrationId = String(payload?.integrationId ?? '')
        const data = payload?.data

        if (Array.isArray(state.listItems?.items) && integrationId) {
          const idx = state.listItems.items.findIndex((x) => String(x?.id) === integrationId)
          if (idx >= 0) {
            if (data && typeof data === 'object') {
              state.listItems.items[idx] = data
            } else {
              state.listItems.items[idx] = {
                ...state.listItems.items[idx],
                is_enabled: !Boolean(state.listItems.items[idx]?.is_enabled),
              }
            }
          }
        }
      })
      .addCase(toggleCompanyIntegrations.rejected, (state, { payload }) => {
        state.toggleStatus = 'failed'
        state.toggleError = payload || 'Failed to toggle integration activation'
      })
  },
})

export const {
  setListItems,
  resetListItems,
  resetCreateState,
  resetUpdateState,
  resetToggleState,
  resetCurrentIntegrationState,
} = slice.actions

export const setCompanyIntegrationsListItems = setListItems
export const resetCompanyIntegrationsListItems = resetListItems
export const resetCreateCompanyIntegrationState = resetCreateState
export const resetUpdateCompanyIntegrationState = resetUpdateState
export const resetCurrentCompanyIntegrationState = resetCurrentIntegrationState

export default slice.reducer